import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { JWTTokenService } from '../services/jwt-token.service';
import { environment } from '../../environments/environment';
import { LoggerService } from '../services/logger.service';
import { AuthStateService } from '../services/auth-state.service';

export const apiHttpInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(JWTTokenService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const authState = inject(AuthStateService);

  // Does not intercept requests for assets
  if (
    req.url.startsWith('assets/') ||
    req.url.startsWith('./assets/') ||
    req.url.startsWith('/assets/')
  ) {
    return next(req);
  }

  const headers: Record<string, string> = {};

  // Content-Type for JSON requests
  if (!(req.body instanceof FormData)) {
    headers['Content-Type'] =
      req.headers.get('Content-type') ?? 'application/ld+json';
  }

  // Special content type for PATCH requests
  if (req.method === 'PATCH') {
    const contentType =
      req.headers.get('Content-type') ?? 'application/merge-patch+json';
    headers['Content-Type'] = contentType;
  }

  // Clone request with API base URL and credentials
  // In development with proxy, environment.api is empty - keep relative URLs
  const authReq = req.clone({
    url: environment.api ? environment.api + req.url : req.url,
    setHeaders: headers,
    withCredentials: true, // CRITICAL: Send httpOnly cookies
  });

  return next(authReq).pipe(
    catchError((err) => {
      logger.error('HTTP request failed', err, {
        url: req.url,
        method: req.method,
        status: err.status,
      });

      if (err.status === 401) {
        // Token expired or invalid - clear authentication state and redirect to login
        tokenService.setToken(null);
        authState.clearAuthState();
        router.navigateByUrl('/login');
      }

      throw err;
    }),
  );
};
